/**
 * Editor service — OutpostForge frontend
 * Pure helpers behind the slotting editor: InstancePools JSON parsing,
 * proposal validation for asset scope (full slotting-core validation with
 * the asset's real MaxVcpus) and outpost/order scope (generation types +
 * total vCPU capacity across ACTIVE assets).
 */

import {
  INSTANCE_CATALOG,
  validateDroplet,
  type DropletValidationResult,
  type FamilyData,
  type InstanceMap,
  type InstanceSize,
} from 'slotting-core';
import type { AssetSummary } from '../types/outposts';

// --- InstancePools JSON (StartCapacityTask-compatible) ---

export type ParseResult =
  | { ok: true; instances: InstanceMap }
  | { ok: false; error: string };

/**
 * Parse an InstancePools JSON document. Accepts either the bare list
 * [{"InstanceType": "...", "Count": n}] or an object with an InstancePools
 * member (the StartCapacityTask request shape).
 */
export function parseInstancePoolsJson(text: string): ParseResult {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return { ok: false, error: 'Invalid JSON document' };
  }

  const pools = Array.isArray(data)
    ? data
    : (data as { InstancePools?: unknown })?.InstancePools;
  if (!Array.isArray(pools) || pools.length === 0) {
    return { ok: false, error: 'Expected a non-empty InstancePools list' };
  }

  const instances: InstanceMap = {};
  for (let i = 0; i < pools.length; i++) {
    const pool = pools[i] as { InstanceType?: unknown; Count?: unknown };
    if (typeof pool?.InstanceType !== 'string' || pool.InstanceType.length === 0) {
      return { ok: false, error: `InstancePools[${i}].InstanceType is required` };
    }
    if (
      typeof pool.Count !== 'number' ||
      !Number.isInteger(pool.Count) ||
      pool.Count < 0 ||
      pool.Count > 9999
    ) {
      return {
        ok: false,
        error: `InstancePools[${i}].Count must be an integer between 0 and 9999`,
      };
    }
    if (instances[pool.InstanceType] !== undefined) {
      return { ok: false, error: `Duplicate instance type '${pool.InstanceType}'` };
    }
    instances[pool.InstanceType] = pool.Count;
  }

  return { ok: true, instances };
}

/** Serialize an instance map as StartCapacityTask InstancePools. */
export function toInstancePools(
  instances: InstanceMap,
): { InstanceType: string; Count: number }[] {
  return Object.entries(instances)
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([InstanceType, Count]) => ({ InstanceType, Count }));
}

// --- Catalog helpers ---

export interface TypeSpec extends InstanceSize {
  family: string;
}

/** All instance types of a catalog generation, keyed by type name. */
export function specsForGeneration(generation: string): Map<string, TypeSpec> {
  const map = new Map<string, TypeSpec>();
  const families = INSTANCE_CATALOG[generation] ?? {};
  for (const [family, familyData] of Object.entries(families)) {
    for (const size of familyData.sizes) {
      map.set(size.name, { ...size, family });
    }
  }
  return map;
}

// --- Asset-scope validation ---

/**
 * Validate a proposed configuration for one asset: full slotting-core
 * validation (capacity, homogeneous, NUMA) with the droplet vCPU capacity
 * overridden by the asset's real MaxVcpus from the API.
 */
export function validateAssetProposal(
  instances: InstanceMap,
  familyData: FamilyData,
  generation: string,
  maxVcpus?: number,
): DropletValidationResult {
  const effectiveFamily: FamilyData =
    maxVcpus !== undefined
      ? {
          ...familyData,
          dropletCapacity: { ...familyData.dropletCapacity, vCPUs: maxVcpus },
        }
      : familyData;
  return validateDroplet({ family: 'edit', instances }, effectiveFamily, generation);
}

// --- Outpost/order-scope validation ---

export interface OutpostProposalValidation {
  valid: boolean;
  message: string;
  totalVcpus: number;
  capacityVcpus: number;
  /** Per-family usage vs capacity (one entry per active family group). */
  families: FamilyUsage[];
}

/** Sum of MaxVcpus across ACTIVE COMPUTE assets (the placeable capacity). */
export function activeVcpuCapacity(assets: AssetSummary[]): number {
  return assets
    .filter((a) => a.assetType === 'COMPUTE' && a.state === 'ACTIVE')
    .reduce((acc, a) => acc + (a.maxVcpus ?? 0), 0);
}

/**
 * Families supported by ACTIVE COMPUTE assets (lowercased). These are the
 * compute droplets the customer actually controls — the outpost-scope
 * editor only offers instance types of these families (bare-metal and
 * networking catalog entries are not slottable capacity).
 */
export function activeComputeFamilies(assets: AssetSummary[]): Set<string> {
  const families = new Set<string>();
  for (const asset of assets) {
    if (asset.assetType !== 'COMPUTE' || asset.state !== 'ACTIVE') continue;
    for (const family of asset.instanceFamilies) {
      families.add(family.toLowerCase());
    }
  }
  return families;
}

/**
 * One instance family the customer controls on this Outpost: its types,
 * the ACTIVE assets backing it, and their aggregate capacity. Placement is
 * family-bound (an m7i instance only fits an m7i droplet), so capacity is
 * tracked per family — not as one Outpost-wide number.
 */
export interface FamilyGroup {
  family: string;
  specs: TypeSpec[];
  assetCount: number;
  /** Sum of MaxVcpus reported by the API for the backing ACTIVE assets. */
  vcpuCapacity: number;
  /** assetCount × the family's droplet memory capacity (catalog). */
  memoryCapacity: number;
}

/** Family groups for the outpost-scope editor, ordered by family name. */
export function buildFamilyGroups(assets: AssetSummary[], generation: string): FamilyGroup[] {
  const catalog = INSTANCE_CATALOG[generation] ?? {};
  const groups: FamilyGroup[] = [];
  for (const [family, familyData] of Object.entries(catalog)) {
    const backing = assets.filter(
      (asset) =>
        asset.assetType === 'COMPUTE' &&
        asset.state === 'ACTIVE' &&
        asset.instanceFamilies.some((f) => f.toLowerCase() === family.toLowerCase()),
    );
    if (backing.length === 0) continue;
    groups.push({
      family,
      specs: familyData.sizes
        .map((size) => ({ ...size, family }))
        .sort((a, b) => a.vCPUs - b.vCPUs),
      assetCount: backing.length,
      vcpuCapacity: backing.reduce(
        (acc, asset) => acc + (asset.maxVcpus ?? familyData.dropletCapacity.vCPUs),
        0,
      ),
      memoryCapacity: backing.length * familyData.dropletCapacity.memoryGiB,
    });
  }
  return groups.sort((a, b) => a.family.localeCompare(b.family));
}

export interface FamilyUsage {
  family: string;
  vcpusUsed: number;
  memoryUsed: number;
  vcpuCapacity: number;
  memoryCapacity: number;
}

/** Proposed vCPU/memory usage of one family against its capacity. */
export function familyUsage(instances: InstanceMap, group: FamilyGroup): FamilyUsage {
  let vcpusUsed = 0;
  let memoryUsed = 0;
  for (const spec of group.specs) {
    const count = instances[spec.name] ?? 0;
    vcpusUsed += spec.vCPUs * count;
    memoryUsed += spec.memoryGiB * count;
  }
  return {
    family: group.family,
    vcpusUsed,
    memoryUsed,
    vcpuCapacity: group.vcpuCapacity,
    memoryCapacity: group.memoryCapacity,
  };
}

/** Aggregate the current configuration across ACTIVE COMPUTE assets. */
export function aggregateCurrentConfig(assets: AssetSummary[]): InstanceMap {
  const instances: InstanceMap = {};
  for (const asset of assets) {
    if (asset.assetType !== 'COMPUTE' || asset.state !== 'ACTIVE') continue;
    for (const capacity of asset.instanceTypeCapacities) {
      instances[capacity.instanceType] =
        (instances[capacity.instanceType] ?? 0) + capacity.count;
    }
  }
  return instances;
}

/**
 * Validate a proposed configuration for the whole Outpost/order. Placement
 * is family-bound, so the check runs per family group: every type must
 * belong to an active family, and each family's proposed vCPUs/memory must
 * fit the capacity of the assets backing that family. (Per-asset placement
 * is decided by the service.)
 */
export function validateOutpostProposal(
  instances: InstanceMap,
  generation: string,
  groups: FamilyGroup[],
): OutpostProposalValidation {
  const specs = specsForGeneration(generation);
  const families = groups.map((group) => familyUsage(instances, group));
  const totalVcpus = families.reduce((acc, f) => acc + f.vcpusUsed, 0);
  const capacityVcpus = families.reduce((acc, f) => acc + f.vcpuCapacity, 0);
  const base = { totalVcpus, capacityVcpus, families };

  const grouped = new Set(groups.map((group) => group.family.toLowerCase()));
  for (const [instanceType, count] of Object.entries(instances)) {
    if (count <= 0) continue;
    const spec = specs.get(instanceType);
    if (!spec) {
      return {
        ...base,
        valid: false,
        message: `Instance type ${instanceType} is not available on this Outpost generation`,
      };
    }
    if (!grouped.has(spec.family.toLowerCase())) {
      return {
        ...base,
        valid: false,
        message: `Instance family ${spec.family} is not supported by the active compute assets on this Outpost`,
      };
    }
  }

  for (const usage of families) {
    if (usage.vcpusUsed > usage.vcpuCapacity) {
      return {
        ...base,
        valid: false,
        message: `${usage.family}: requested ${usage.vcpusUsed} vCPUs exceed the ${usage.vcpuCapacity} vCPUs of this family's assets`,
      };
    }
    if (usage.memoryUsed > usage.memoryCapacity) {
      return {
        ...base,
        valid: false,
        message: `${usage.family}: requested ${usage.memoryUsed} GiB exceed the ${usage.memoryCapacity} GiB of this family's assets`,
      };
    }
  }

  return { ...base, valid: true, message: '' };
}
